import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { getAuthenticationStatus } from "api-client";
import { observer } from "mobx-react-lite";
import { useEffect, useEffectEvent } from "react";

import { useAuthStore } from "src/auth";

export const AuthenticationStatusMonitor = observer(
  function AuthenticationStatusMonitor() {
    const authStore = useAuthStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const authenticationStatusQuery = useQuery({
      queryKey: ["authentication-status"],
      queryFn: async () => {
        const result = await getAuthenticationStatus({ throwOnError: false });

        if (result.response?.status === 401) {
          return false;
        }

        if (result.error) {
          throw result.error;
        }

        return true;
      },
      enabled: authStore.isAuthenticated,
      refetchOnWindowFocus: true,
      retry: false,
    });

    const refetchAuthenticationStatus = useEffectEvent(() => {
      if (authStore.isAuthenticated) {
        void authenticationStatusQuery.refetch();
      }
    });

    useEffect(() => {
      window.addEventListener("focus", refetchAuthenticationStatus);

      return () => {
        window.removeEventListener("focus", refetchAuthenticationStatus);
      };
    }, []);

    useEffect(() => {
      if (authenticationStatusQuery.data !== false) {
        return;
      }

      authStore.clear();
      queryClient.clear();
      void navigate({ to: "/sign-in", replace: true });
    }, [authStore, authenticationStatusQuery.data, navigate, queryClient]);

    return null;
  },
);
